import { Field, Formik, useFormikContext } from "formik";
import Form from "@poseidon/ui/components/form/FullWidthForm";
import {
  FormikAutocomplete,
  FullWidthField,
  SummaryTable,
  SummaryTableRow,
} from "@poseidon/ui/components";
import { CurrencyField } from "@poseidon/ui/components/form/CurrencyField";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
} from "@mui/material";
import { green } from "@mui/material/colors";
import * as Yup from "yup";
import { formatCurrency } from "@poseidon/utils/currency";
import {
  useBoatMakes,
  useBoatModels,
  useBoatTypes,
} from "../../Boats/BoatOptionsContext";
import { useQuote } from "../../LoanCalculator/useQuote";

const initialValues = {
  costOfVessel: "",
  deposit: "",
  termMonths: "",
  balloon: "",
  boatType: null,
  make: "",
  model: "",
};

const validationSchema = Yup.object().shape({
  costOfVessel: Yup.number()
    .typeError("Enter a number")
    .positive("Must be greater than 0")
    .required("Required"),
  deposit: Yup.number()
    .typeError("Enter a number")
    .min(0, "Cannot be negative")
    .lessThan(
      Yup.ref("costOfVessel"),
      "Deposit must be less than the vessel cost"
    )
    .required("Required"),
  termMonths: Yup.number()
    .typeError("Enter a number")
    .integer("Whole months only")
    .positive("Must be greater than 0")
    .required("Required"),
  // Optional balloon (larger final instalment). Mirrors the backend rule
  // 0 <= balloon < principal, where principal = costOfVessel - deposit.
  balloon: Yup.number()
    .transform((value, original) =>
      String(original).trim() === "" ? undefined : value
    )
    .typeError("Enter a number")
    .min(0, "Balloon must not be negative")
    .test(
      "less-than-principal",
      "Balloon must be less than the loan principal",
      function (value) {
        if (value == null) {
          return true;
        }
        const principal =
          Number(this.parent.costOfVessel) - Number(this.parent.deposit);
        if (!Number.isFinite(principal)) {
          return true;
        }
        return value < principal;
      }
    )
    .notRequired(),
  boatType: Yup.object().nullable().required("Select a boat type"),
  // make/model are intentionally NOT validated here so a quote can be
  // calculated without them; they are required at the Continue step instead.
});

// Extracts the boat details (type/make/model) from the form to store on the
// application's linked boat document.
const toBoatDetails = (values) => ({
  type: values.boatType?.value ?? "",
  make: values.make?.trim() ?? "",
  model: values.model?.trim() ?? "",
});

// Cascading Type -> Make -> Model selectors. Make/Model are catalog-driven
// (from the boatModels collection) but freeSolo, so an admin can type a boat
// that isn't in the catalog. Selecting a type resets make/model; selecting a
// make resets model.
function BoatCatalogFields() {
  const { values, setFieldValue } = useFormikContext();
  const boatTypes = useBoatTypes();
  const boatMakes = useBoatMakes();
  const boatModels = useBoatModels();

  const typeValue = values.boatType?.value;
  const makeOptions = (boatMakes?.[typeValue] ?? []).map((o) => o.value);
  const modelOptions = (boatModels?.[typeValue]?.[values.make] ?? []).map(
    (o) => o.value
  );

  return (
    <>
      <Field
        component={FormikAutocomplete}
        name="boatType"
        label="Boat Type"
        margin="normal"
        options={boatTypes}
        getOptionLabel={(option) => option?.label ?? ""}
        isOptionEqualToValue={(option, value) => option.value === value?.value}
        onChange={(_, value) => {
          setFieldValue("boatType", value);
          setFieldValue("make", "");
          setFieldValue("model", "");
        }}
      />
      <Field
        component={FormikAutocomplete}
        name="make"
        label="Make"
        margin="normal"
        freeSolo
        autoSelect
        options={makeOptions}
        onInputChange={(_, value) => {
          setFieldValue("make", value ?? "");
          setFieldValue("model", "");
        }}
      />
      <Field
        component={FormikAutocomplete}
        name="model"
        label="Model"
        margin="normal"
        freeSolo
        autoSelect
        options={modelOptions}
        onInputChange={(_, value) => setFieldValue("model", value ?? "")}
      />
    </>
  );
}

export function QuoteCalculatorStep({ onContinue, onCancel }) {
  const { getQuote, saving, error, success, quote } = useQuote();
  const flatQuote = quote?.data?.quote;

  const onSubmit = (values) => {
    const balloon = Number(values.balloon);
    return getQuote({
      costOfVessel: Number(values.costOfVessel),
      deposit: Number(values.deposit),
      termMonths: Number(values.termMonths),
      boatType: values.boatType?.value,
      // omit when unset so a standard quote sends the exact payload as before
      ...(balloon > 0 ? { balloon } : {}),
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ values, setFieldTouched, setFieldError }) => {
        // make/model are optional for the quote but required to continue.
        const handleContinue = () => {
          const make = values.make?.trim();
          const model = values.model?.trim();
          let valid = true;
          if (!make) {
            setFieldTouched("make", true, false);
            setFieldError("make", "Enter a make");
            valid = false;
          }
          if (!model) {
            setFieldTouched("model", true, false);
            setFieldError("model", "Enter a model");
            valid = false;
          }
          if (!valid) {
            return;
          }
          onContinue(flatQuote, toBoatDetails(values));
        };
        return (
          <Form>
            <DialogContent>
              <Field
                component={CurrencyField}
                name="costOfVessel"
                label="Cost of Vessel"
                margin="normal"
              />
              <Field
                component={CurrencyField}
                name="deposit"
                label="Deposit"
                margin="normal"
              />
              <Field
                component={FullWidthField}
                type="number"
                name="termMonths"
                label="Term (months)"
                margin="normal"
              />
              <Field
                component={CurrencyField}
                name="balloon"
                label="Balloon (optional)"
                margin="normal"
                helperText="Larger final instalment. Leave blank for a standard amortising loan."
              />
              <BoatCatalogFields />
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error.message}
                </Alert>
              )}
              {success && flatQuote && (
                <Box sx={{ mt: 3 }}>
                  {flatQuote.fixedRate && (
                    <Chip label="Fixed rate" color="info" sx={{ mb: 2 }} />
                  )}
                  <SummaryTable>
                    <SummaryTableRow label="Amount of Credit">
                      {formatCurrency(flatQuote.principal)}
                    </SummaryTableRow>
                    <SummaryTableRow label="Monthly Payment">
                      {formatCurrency(flatQuote.monthlyPayment, true)}
                    </SummaryTableRow>
                    {flatQuote.balloon > 0 && (
                      <SummaryTableRow label="Final Payment (incl. balloon)">
                        {formatCurrency(
                          flatQuote.monthlyPayment + flatQuote.balloon,
                          true
                        )}
                      </SummaryTableRow>
                    )}
                    {flatQuote.balloon > 0 && (
                      <SummaryTableRow label="Balloon">
                        {formatCurrency(flatQuote.balloon, true)}
                      </SummaryTableRow>
                    )}
                    <SummaryTableRow label="APR">
                      {flatQuote.apr}%
                    </SummaryTableRow>
                    <SummaryTableRow label="Annual Rate">
                      {flatQuote.annualRate}% p.a.
                    </SummaryTableRow>
                    <SummaryTableRow label="Total Payable">
                      {formatCurrency(flatQuote.totalPayable, true)}
                    </SummaryTableRow>
                    <SummaryTableRow label="Cost of Credit">
                      {formatCurrency(flatQuote.costOfCredit, true)}
                    </SummaryTableRow>
                  </SummaryTable>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={onCancel} disabled={saving}>
                Cancel
              </Button>
              <Box sx={{ position: "relative" }}>
                <Button
                  type="submit"
                  variant={success ? "outlined" : "contained"}
                  disabled={saving}
                >
                  {success ? "Recalculate" : "Calculate Quote"}
                </Button>
                {saving && (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: green[500],
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      mt: "-12px",
                      ml: "-12px",
                    }}
                  />
                )}
              </Box>
              {success && flatQuote && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleContinue}
                >
                  Continue
                </Button>
              )}
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );
}
